/**
 * Duvar core (pure TS, no Phaser/three, no Math.random): a wall of blocks stands in front of a
 * hidden picture. Every block carries an arrow; tapping slides it out along the arrow if nothing
 * is in the way, otherwise it bumps. A picture cell shows once its whole column (all depth
 * layers) is gone. Guess the picture with as few taps as you can.
 *
 * Axes: x = column (0 left), y = row (0 bottom), z = depth layer (0 against the picture,
 * d-1 nearest the player). Arrows: ±x, ±y, +z (toward the player). Never −z: the picture is behind.
 */
import { matchesAnswer, normalizeAnswer, type Picture } from "@enjoyment/core";

export type Dir = 0 | 1 | 2 | 3 | 4;
/** Unit steps per arrow: right, left, up, down, out (toward the player). */
export const DIRS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
];

export interface Block {
  id: number;
  x: number;
  y: number;
  z: number;
  dir: Dir;
}

export interface DuvarLevel {
  n: number;
  seed: number;
  w: number;
  h: number;
  d: number;
  blocks: Block[];
  theme: string;
  /** k-th visit of the theme → k-th picture of that theme's fixed order. */
  pictureIndex: number;
}

export interface DuvarState {
  level: DuvarLevel;
  picture: Picture;
  /** cell index → block id, or -1 when empty. */
  occ: Int32Array;
  present: boolean[];
  left: number;
  /** Every tap on a block, exits and bumps alike: the shareable number. */
  taps: number;
  bumps: number;
  wrong: number;
  wrongLabels: string[];
  status: "playing" | "won" | "lost";
  solvedAtTaps: number;
  revealedAtSolve: number;
}

/** A wrong guess costs as much as this many taps in the shared result. */
export const WRONG_GUESS_TAPS = 2;

export const cellIndex = (l: Pick<DuvarLevel, "w" | "h">, x: number, y: number, z: number): number => (z * l.h + y) * l.w + x;

export function createGame(level: DuvarLevel, picture: Picture): DuvarState {
  const occ = new Int32Array(level.w * level.h * level.d).fill(-1);
  for (const b of level.blocks) occ[cellIndex(level, b.x, b.y, b.z)] = b.id;
  return {
    level,
    picture,
    occ,
    present: level.blocks.map(() => true),
    left: level.blocks.length,
    taps: 0,
    bumps: 0,
    wrong: 0,
    wrongLabels: [],
    status: "playing",
    solvedAtTaps: 0,
    revealedAtSolve: 0,
  };
}

/** First block in the way of `id`'s arrow, or -1 when its path out is clear. */
export function blockerOf(s: Pick<DuvarState, "level" | "occ">, id: number): number {
  const b = s.level.blocks[id]!;
  const [dx, dy, dz] = DIRS[b.dir]!;
  const { w, h, d } = s.level;
  for (let x = b.x + dx, y = b.y + dy, z = b.z + dz; x >= 0 && x < w && y >= 0 && y < h && z >= 0 && z < d; x += dx, y += dy, z += dz) {
    const hit = s.occ[cellIndex(s.level, x, y, z)]!;
    if (hit >= 0) return hit;
  }
  return -1;
}

export function isFree(s: DuvarState, id: number): boolean {
  return s.present[id] === true && blockerOf(s, id) < 0;
}

export function freeBlocks(s: DuvarState): number[] {
  const out: number[] = [];
  for (const b of s.level.blocks) if (s.present[b.id] && blockerOf(s, b.id) < 0) out.push(b.id);
  return out;
}

export function columnOpen(s: Pick<DuvarState, "level" | "occ">, x: number, y: number): boolean {
  for (let z = 0; z < s.level.d; z++) if (s.occ[cellIndex(s.level, x, y, z)]! >= 0) return false;
  return true;
}

export function revealedCount(s: Pick<DuvarState, "level" | "occ">): number {
  let n = 0;
  for (let y = 0; y < s.level.h; y++) for (let x = 0; x < s.level.w; x++) if (columnOpen(s, x, y)) n++;
  return n;
}

export function revealedFraction(s: Pick<DuvarState, "level" | "occ">): number {
  return revealedCount(s) / (s.level.w * s.level.h);
}

export interface TapResult {
  kind: "exit" | "bump" | "none";
  /** The block that stopped a bump. */
  blocker: number;
  /** Picture cells that opened with this exit. */
  opened: { x: number; y: number }[];
}

/** Tap a block. Exits leave the wall; bumps count as taps but change nothing else. */
export function tapBlock(s: DuvarState, id: number): TapResult {
  if (s.status !== "playing" || !s.present[id]) return { kind: "none", blocker: -1, opened: [] };
  s.taps++;
  const blocker = blockerOf(s, id);
  if (blocker >= 0) {
    s.bumps++;
    return { kind: "bump", blocker, opened: [] };
  }
  const b = s.level.blocks[id]!;
  s.present[id] = false;
  s.occ[cellIndex(s.level, b.x, b.y, b.z)] = -1;
  s.left--;
  const opened = columnOpen(s, b.x, b.y) ? [{ x: b.x, y: b.y }] : [];
  return { kind: "exit", blocker: -1, opened };
}

export interface GuessOutcome {
  correct: boolean;
  /** Same wrong answer again: echoed back, costs nothing. */
  repeat: boolean;
}

export function guessAnswer(s: DuvarState, text: string): GuessOutcome {
  if (s.status !== "playing") return { correct: false, repeat: false };
  if (matchesAnswer(s.picture, text)) {
    s.status = "won";
    s.solvedAtTaps = s.taps;
    s.revealedAtSolve = revealedFraction(s);
    return { correct: true, repeat: false };
  }
  const n = normalizeAnswer(text);
  if (!n || s.wrongLabels.some((w) => normalizeAnswer(w) === n)) return { correct: false, repeat: true };
  s.wrong++;
  s.wrongLabels.push(text.trim());
  return { correct: false, repeat: false };
}

/** "Show me": the round ends without a win (counts toward the free skip, H4). */
export function giveUp(s: DuvarState): void {
  if (s.status === "playing") s.status = "lost";
}

/** The shareable number: taps at the correct guess, wrong guesses included. */
export function resultTaps(s: DuvarState): number {
  return s.solvedAtTaps + s.wrong * WRONG_GUESS_TAPS;
}

/** Map stars: name the picture while little of it shows, with few bumps and no wrong answers. */
export function starsFor(s: DuvarState): 1 | 2 | 3 {
  const shown = s.revealedAtSolve;
  if (shown <= 0.4 && s.wrong === 0 && s.bumps <= 2) return 3;
  if (shown <= 0.65 && s.wrong <= 1) return 2;
  return 1;
}

export type Move = { kind: "tap"; id: number } | { kind: "guess"; text: string } | { kind: "giveUp" };

/** Re-run a recorded game; illegal moves change nothing (a tampered log cannot cheat the core). */
export function replay(level: DuvarLevel, picture: Picture, moves: readonly Move[]): DuvarState {
  const s = createGame(level, picture);
  for (const m of moves) {
    if (m.kind === "tap") tapBlock(s, m.id);
    else if (m.kind === "guess") guessAnswer(s, m.text);
    else giveUp(s);
  }
  return s;
}

/** Spoiler-free share grid: 🟩 open cells, ⬛ still covered at the guess (top row first). */
export function shareGrid(s: DuvarState, on = "🟩", off = "⬛"): string {
  const rows: string[] = [];
  for (let y = s.level.h - 1; y >= 0; y--) {
    let row = "";
    for (let x = 0; x < s.level.w; x++) row += columnOpen(s, x, y) ? on : off;
    rows.push(row);
  }
  return rows.join("\n");
}
